import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { cn } from '../lib/cn.js'
import { Icone } from './Icone.js'

/**
 * Usa <dialog> nativo: foco preso, Esc fechando e fundo inerte vêm do
 * navegador, sem reimplementar armadilha de foco em JavaScript — que é onde
 * modal caseiro costuma falhar para quem usa teclado.
 */
export type ModalProps = {
  aberto: boolean
  aoFechar: () => void
  titulo: string
  children: React.ReactNode
  rodape?: React.ReactNode | undefined
  tamanho?: 'sm' | 'md' | 'lg' | undefined
  /** Bottom sheet no mobile: é como o ProductSheet aparece no celular. */
  folha?: boolean | undefined
  className?: string | undefined
}

export function Modal({
  aberto, aoFechar, titulo, children, rodape, tamanho = 'md', folha, className,
}: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (aberto && !el.open) el.showModal()
    if (!aberto && el.open) el.close()
  }, [aberto])

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const aoCancelar = (e: Event) => {
      e.preventDefault() // deixa o React decidir, em vez de o dialog fechar sozinho
      aoFechar()
    }
    el.addEventListener('cancel', aoCancelar)
    return () => el.removeEventListener('cancel', aoCancelar)
  }, [aoFechar])

  return (
    <dialog
      ref={ref}
      className={cn('du-modal', `du-modal--${tamanho}`, folha && 'du-modal--folha', className)}
      aria-label={titulo}
      // Clique no fundo fecha; clique no conteúdo não (o alvo é o próprio dialog).
      onClick={(e) => { if (e.target === ref.current) aoFechar() }}
    >
      <div className="du-modal__caixa">
        <header className="du-modal__topo">
          <h2 className="du-modal__titulo">{titulo}</h2>
          <button type="button" className="du-modal__fechar" onClick={aoFechar} aria-label="Fechar">
            <Icone de={X} tamanho={24} />
          </button>
        </header>
        <div className="du-modal__corpo">{children}</div>
        {rodape ? <footer className="du-modal__rodape">{rodape}</footer> : null}
      </div>
    </dialog>
  )
}
